import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EntityFormDialog } from "../Dialog/EntityFormDialog";
import { Button } from "../Button/Button";
import { getApiToken } from "../../data/api/token";
import {
  getOrderInstructions,
  saveOrderInstructions,
  OrderInstructions,
} from "../../api/orderInstructions";

export function OrderInstructionsDialog({
  orderId,
  workspaceId,
  onClose,
}: {
  orderId: string;
  workspaceId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation("orders");
  const [data, setData] = useState<OrderInstructions>();
  const [values, setValues] = useState<Record<number, string>>({});
  const [reload, setReload] = useState(0);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    setData(undefined);
    setError(false);
    getApiToken()
      .then((token) => getOrderInstructions(token, workspaceId, orderId))
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setValues(
            Object.fromEntries(
              result.stops.map((s) => [s.stopNumber, s.instructions]),
            ),
          );
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, workspaceId, reload]);
  const changes =
    data?.stops
      .filter((s) => s.editable && values[s.stopNumber] !== s.instructions)
      .map((s) => ({
        stopNumber: s.stopNumber,
        instructions: values[s.stopNumber],
      })) || [];
  const save = async () => {
    if (!data || !changes.length) return;
    setBusy(true);
    setError(false);
    try {
      await saveOrderInstructions(
        await getApiToken(),
        workspaceId,
        orderId,
        data.fingerprint,
        changes,
      );
      if (mounted.current) onClose();
    } catch {
      if (mounted.current) setError(true);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  return (
    <EntityFormDialog
      title={t("instructions.title")}
      show
      onCancel={() => {
        if (!busy) onClose();
      }}
    >
      <p className="mb-5 text-sm text-muted">{t("instructions.help")}</p>
      {!data && !error && (
        <p role="status" className="py-8 text-center text-muted">
          {t("instructions.loading")}
        </p>
      )}
      <div className="max-h-[55vh] space-y-4 overflow-y-auto pr-1">
        {data?.stops.map((stop, index) => (
          <div
            key={stop.stopNumber}
            className="rounded-xl border border-border bg-surface p-4"
          >
            <div className="mb-3 flex gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-muted text-sm font-semibold">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <label
                  htmlFor={`instructions-${stop.stopNumber}`}
                  className="block text-sm font-semibold"
                >
                  {t(
                    stop.isPickup === true
                      ? "instructions.pickup"
                      : stop.isPickup === false
                        ? "instructions.delivery"
                        : "instructions.stop",
                  )}
                  {stop.location.name ? ` · ${stop.location.name}` : ""}
                </label>
                <p className="mt-1 text-sm text-muted">
                  {[
                    stop.location.addressLine,
                    [stop.location.postCode, stop.location.city]
                      .filter(Boolean)
                      .join(" "),
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              </div>
              {!stop.editable && (
                <span className="text-xs text-muted">
                  {t("instructions.readonly")}
                </span>
              )}
            </div>
            <textarea
              id={`instructions-${stop.stopNumber}`}
              rows={3}
              maxLength={4000}
              disabled={busy || !stop.editable}
              value={values[stop.stopNumber] ?? ""}
              onChange={(e) =>
                setValues((current) => ({
                  ...current,
                  [stop.stopNumber]: e.target.value,
                }))
              }
              placeholder={t("instructions.placeholder")}
              className="block w-full resize-y rounded-lg border border-border bg-surface p-3 text-sm text-text focus:border-brand focus:ring-brand disabled:opacity-60"
            />
            <div className="mt-1 text-right text-xs text-muted">
              {(values[stop.stopNumber] || "").length}/4000
            </div>
          </div>
        ))}
        {data?.stops.length === 0 && (
          <p className="py-6 text-sm text-muted">{t("instructions.empty")}</p>
        )}
      </div>
      {error && (
        <div role="alert" className="mt-4 text-sm text-red-600">
          {t("instructions.error")}{" "}
          <button
            type="button"
            className="underline"
            disabled={busy}
            onClick={() => setReload((n) => n + 1)}
          >
            {t("instructions.reload")}
          </button>
        </div>
      )}
      <div className="mt-5 flex justify-end gap-3 border-t border-border pt-4">
        <Button variant="secondary" disabled={busy} onClick={onClose}>
          {t("instructions.cancel")}
        </Button>
        <Button
          variant="primary"
          disabled={busy || !changes.length}
          onClick={save}
        >
          {t(busy ? "instructions.saving" : "instructions.save")}
        </Button>
      </div>
    </EntityFormDialog>
  );
}
